import type { CompletionLevel, EntryKind, GoalCategory, MeasureShape } from './events.js';
import type { FeatureFlags } from './features.js';
import { expectedWeight, isScheduledOn } from './schedule.js';
import { flavourFor, flavourForPath } from './flavour.js';
import type { Flavour } from './flavour.js';
import type { RankId, Ruleset, TitleId } from './ruleset.js';
import type { DerivedState, Entry, Task } from './state.js';
import { completionKey, orderedHabits } from './state.js';
import { isStreakAlive } from './reduce.js';
import { dayIndex, daysBetween } from './time.js';
import type { StudyDate } from './time.js';

/**
 * SELECTORS — the only shape the UI is allowed to read.
 *
 * Screens never touch `DerivedState` directly. That indirection is what lets
 * the state shape, the reducer, or the storage engine change without a single
 * component edit — and it means every number on screen has one tested
 * definition rather than being assembled ad hoc in six places.
 *
 * Everything here is pure and clock-free: callers pass `today`, because core
 * has no clock.
 */

export type CellState =
  | 'done'
  /** Kept at the floor — a kept day, drawn differently from a full one. */
  | 'floor'
  /** Scheduled, in the past, not done. */
  | 'missed'
  /** Not scheduled that day — rendered muted, never as a failure. */
  | 'off'
  | 'future';

export type HistoryCell = { date: StudyDate; state: CellState };

export type HabitRowView = {
  id: string;
  name: string;
  icon?: string;
  done: boolean;
  /** Which level today was kept at, or null. `done` is `level !== null`. */
  level: CompletionLevel | null;
  /** The two-minute version, when the student wrote one. */
  floor?: string;
  /** The if-then plan, when there is one. */
  anchor?: string;
  place?: string;
  scheduledToday: boolean;
  history: HistoryCell[];
};

export type TaskView = {
  id: string;
  name: string;
  track?: string;
  dueDate?: StudyDate;
  overdue: boolean;
  done: boolean;
};

/**
 * The goal, as the UI reads it (§53.4).
 *
 * Progress is PARTS, never elapsed time. A day-based ratio advances whether
 * or not the student works — reassuring when ahead, an accusation when
 * behind, informative never. Parts move only when work happens.
 *
 * Every parts-derived field is nullable because a goal with no parts is
 * normal: "get fit" cannot honestly be decomposed, and inventing a
 * denominator for it would be a lie. Such a goal shows its destination and
 * its obstacle, and no bar.
 */
export type GoalView = {
  id: string;
  title: string;
  category: GoalCategory;
  /** Shown beside the destination, so the plan is read against what actually
   *  gets in its way rather than as a wish on its own (MCII). */
  obstacle?: string;
  daysLeft: number;
  partsDone: number;
  partsTotal: number;
  /** The ordered work with its completion state, for the breakdown list. */
  parts: { name: string; done: boolean }[];
  /** `partsDone / partsTotal`, or null with no parts. */
  progress: number | null;
  /** The first unfinished part — Today's "Next up", and the deterministic,
   *  offline answer to "what do I do next" that most people open an AI for. */
  nextPart: string | null;
  /** Days available per remaining part if the rest are spread evenly over the
   *  time left. Null with no parts, nothing remaining, or no days left. */
  daysPerPart: number | null;
  /**
   * Parts ahead (+) or behind (-) an even pace, rounded.
   *
   * This is the one field that can read as an accusation, and it is kept
   * because it recomputes: it has no wrong state, only a current one. Miss
   * three days and it does not declare the plan broken — it says the pace
   * tightened. Callers should render it as pace, never as debt.
   */
  drift: number | null;
};

export type WeekDay = {
  date: StudyDate;
  isToday: boolean;
  state: 'complete' | 'partial' | 'none' | 'future';
  /** Share of that day's scheduled habits that got ticked, 0–1. The week
   *  circles fill by this; `state` is only for the colour at the extremes. */
  ratio: number;
  done: number;
  scheduled: number;
};

/**
 * The promise, and whether it was kept.
 *
 * A habit is a promise made in advance: its schedule says how many times a
 * week it will be done, before the week happens. `promised` is the sum of
 * those, `kept` is what actually got ticked, and the ratio between them is the
 * one number in this app that is a fact about the person rather than a score
 * the app invented.
 *
 * It is also the number that survives a bad day. A streak deletes the evidence
 * the moment it breaks; this doesn't.
 */
export type Commitment = {
  /** Habit-days ticked. */
  kept: number;
  /** Habit-days the schedules asked for. Fractional for `timesPerWeek`. */
  promised: number;
  /** `kept / promised`, clamped to 1 — doing more than you promised reads as
   *  keeping your word, never as 130%. Zero when nothing was promised. */
  ratio: number;
  /** Days actually counted. Shorter than asked for when the first habit is
   *  recent, so the caller can refuse to draw a percentage off two days. */
  days: number;
};

export type EntryView = {
  id: string;
  track: string;
  minutes: number;
  kind: EntryKind;
  /** What they set out to do — "ch.4 problems". Captured at log time and,
   *  until now, never shown: it is the most useful thing about an entry and
   *  far more locatable than the bare subject. */
  intention?: string;
  /** Timed entries are shown as such: the minutes were measured, not typed,
   *  and that difference is the one thing a student cannot fake for themselves. */
  timed: boolean;
};

/**
 * A day's evidence.
 *
 * The mirror of `Commitment`: that one says what was promised and kept, this
 * one says what was actually done. Day-scoped on purpose — Home is today, and
 * the full ledger is its own destination.
 */
export type LogView = {
  /** Every minute recorded that day, however it was captured. */
  minutes: number;
  /** The `review` slice of `minutes`. */
  reviewMinutes: number;
  /** Entries that came from a running timer. */
  timedSessions: number;
  /** Newest first, capped. */
  entries: EntryView[];
  /** How many the cap hid. */
  hiddenCount: number;
  /** Per-track totals for the day, biggest first. */
  tracks: { track: string; minutes: number }[];
};

export type Greeting = 'morning' | 'afternoon' | 'evening' | 'night';

export type TodayView = {
  date: StudyDate;
  greeting: Greeting;
  /** null until onboarding captures one — Home greets without a name rather
   *  than inventing a placeholder. */
  name: string | null;
  /** `best` is the longest run ever, and it is here so a broken streak never
   *  has to render as a bare zero — the run happened, and the app that exists
   *  to keep evidence must not be the one that erases it. */
  streak: { current: number; best: number; alive: boolean };
  /** Null until there is enough history for a percentage to mean anything. */
  commitment: Commitment | null;
  power: number;
  rank: RankId;
  rankRatio: number;
  titles: TitleId[];
  habits: HabitRowView[];
  /** Today's scoreboard, counting only what is scheduled today. */
  progress: { done: number; total: number };
  tasks: { items: TaskView[]; hiddenCount: number };
  /** Every minute logged today, however it was captured. */
  minutesToday: number;
  /** Today's evidence. Null when the card is flagged off. */
  log: LogView | null;
  goal: GoalView | null;
  /** Present only on a day that follows a gap. The biggest thing on Home when
   *  it exists — see §45. */
  comeback: ComebackView | null;
  streakAtRisk: boolean;
};

/** Days of history a commitment rate needs before it is worth showing at all.
 *  Below this a single missed morning swings it by double digits. */
export const MIN_COMMITMENT_DAYS = 7;

/** Walk back `count` days from `end`, oldest first. */
function dateRange(end: StudyDate, count: number): StudyDate[] {
  const dates: StudyDate[] = [];
  for (let offset = count - 1; offset >= 0; offset -= 1) dates.push(shiftDate(end, -offset));
  return dates;
}

/** Day arithmetic without touching `Date` — core stays deterministic. */
export function shiftDate(date: StudyDate, days: number): StudyDate {
  const target = dayIndex(date) + days;
  // Inverse of dayIndex; mirrors civilFromDays in time.ts.
  const z = target + 719_468;
  const era = Math.floor(z / 146_097);
  const doe = z - era * 146_097;
  const yoe = Math.floor(
    (doe - Math.floor(doe / 1460) + Math.floor(doe / 36_524) - Math.floor(doe / 146_096)) / 365,
  );
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp < 10 ? mp + 3 : mp - 9;
  const y = yoe + era * 400 + (m <= 2 ? 1 : 0);
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${y}-${pad(m)}-${pad(d)}`;
}

/** The level a habit was kept at, or null if it was not. */
export function completionAt(
  state: DerivedState,
  habitId: string,
  date: StudyDate,
): CompletionLevel | null {
  return state.completions[completionKey(habitId, date)] ?? null;
}

/**
 * Whether the promise was kept at all.
 *
 * A floor day counts. That is the entire point of the floor — the alternative
 * is a day the student did something and the app calls it nothing.
 */
export function isDone(state: DerivedState, habitId: string, date: StudyDate): boolean {
  return completionAt(state, habitId, date) !== null;
}

function cellState(
  state: DerivedState,
  habitId: string,
  date: StudyDate,
  today: StudyDate,
): CellState {
  const habit = state.habits[habitId];
  if (!habit) return 'off';
  const level = completionAt(state, habitId, date);
  if (level) return level === 'floor' ? 'floor' : 'done';
  if (date > today) return 'future';
  if (!isScheduledOn(habit, date)) return 'off';
  return date === today ? 'off' : 'missed';
}

/** The per-row dot strip — the consistency grid rotated into the list. */
export function habitHistory(
  state: DerivedState,
  habitId: string,
  today: StudyDate,
  days: number,
): HistoryCell[] {
  return dateRange(today, days).map((date) => ({
    date,
    state: cellState(state, habitId, date, today),
  }));
}

/** The full grid for the Progress tab. Same cell definition as the row dots,
 *  so the two views can never disagree. */
export type GridRow = {
  id: string;
  name: string;
  cells: CellState[];
  /**
   * The habit's own kept-rate over the window on screen, or null when it is
   * too young to mean anything.
   *
   * This is the most diagnostic number in the product: it names WHICH promise
   * is failing, which is the only thing a student can actually act on. An
   * overall rate of 74% tells you nothing; "Wake early: 40%" tells you what to
   * change or drop.
   */
  keptRatio: number | null;
};

/**
 * One column of the chart, summed down the habits.
 *
 * The mirror of `GridRow.keptRatio`: that reads across a row and names which
 * promise is failing, this reads down a column and says how a single day went.
 * Both are folded from the same cells, so the two can never disagree.
 */
export type GridDay = {
  date: StudyDate;
  /** Habits scheduled that day. 0 is a rest day, never a failure. */
  promised: number;
  kept: number;
  /** `kept / promised`. Null when nothing was promised, so a chart can leave
   *  a gap rather than plunging to zero on a rest day. */
  ratio: number | null;
  future: boolean;
  isToday: boolean;
};

export function habitGrid(
  state: DerivedState,
  today: StudyDate,
  days: number,
): { dates: StudyDate[]; rows: GridRow[]; days: GridDay[] } {
  const dates = dateRange(today, days);
  const rows = orderedHabits(state)
    .filter((habit) => !habit.archived)
    .map((habit) => {
      const kept = commitment(state, today, days, { habitId: habit.id });
      return {
        id: habit.id,
        name: habit.name,
        cells: dates.map((date) => cellState(state, habit.id, date, today)),
        keptRatio: kept.days >= MIN_COMMITMENT_DAYS && kept.promised > 0 ? kept.ratio : null,
      };
    });

  // Folded from the cells already computed above rather than recounted from
  // the habits, so the line above the chart is arithmetic ON the chart.
  const columns: GridDay[] = dates.map((date, index) => {
    let promised = 0;
    let kept = 0;
    let future = false;

    for (const row of rows) {
      const cell = row.cells[index];
      if (cell === 'future') future = true;
      if (cell === 'done' || cell === 'floor') {
        promised += 1;
        kept += 1;
      } else if (cell === 'missed') {
        promised += 1;
      }
    }

    const isToday = date === today;

    return {
      date,
      promised,
      kept,
      // Today is deliberately unplottable. The day is not over, so an
      // unticked habit is not yet a miss — `cellState` reports it as `off`,
      // which would score today at 100% off whatever happens to be done
      // already and draw a line that flatters. The same reason `commitment`
      // ends yesterday.
      ratio: isToday || promised === 0 ? null : kept / promised,
      future,
      isToday,
    };
  });

  return { dates, rows, days: columns };
}

export function weekStrip(state: DerivedState, today: StudyDate, days = 7): WeekDay[] {
  const habits = Object.values(state.habits);
  return dateRange(today, days).map((date) => {
    if (date > today) {
      return { date, isToday: false, state: 'future' as const, ratio: 0, done: 0, scheduled: 0 };
    }
    const scheduled = habits.filter((habit) => isScheduledOn(habit, date));
    const done = scheduled.filter((habit) => isDone(state, habit.id, date)).length;
    const status =
      scheduled.length > 0 && done === scheduled.length
        ? ('complete' as const)
        : done > 0
          ? ('partial' as const)
          : ('none' as const);
    return {
      date,
      isToday: date === today,
      state: status,
      ratio: scheduled.length === 0 ? 0 : done / scheduled.length,
      done,
      scheduled: scheduled.length,
    };
  });
}

function toTaskView(task: Task, today: StudyDate): TaskView {
  return {
    id: task.id,
    name: task.name,
    done: task.done,
    overdue: !task.done && task.dueDate !== undefined && task.dueDate < today,
    ...(task.track === undefined ? {} : { track: task.track }),
    ...(task.dueDate === undefined ? {} : { dueDate: task.dueDate }),
  };
}

/**
 * Today's tasks plus anything overdue — never the whole backlog (§4).
 *
 * A task with no due date is standing work: it is live every day until it is
 * done, which is what lets someone add "Revise chemistry" without first
 * deciding when. A dated one appears on its day and stays, marked overdue.
 */
export function openTasks(state: DerivedState, today: StudyDate): TaskView[] {
  const relevant = (task: Task) =>
    !task.archived && !task.done && (task.dueDate === undefined || task.dueDate <= today);

  return Object.values(state.tasks)
    .filter(relevant)
    .map((task) => toTaskView(task, today))
    .sort((a, b) => Number(b.overdue) - Number(a.overdue));
}

/**
 * What was finished today, still on today's list.
 *
 * Ticked tasks don't vanish, for the same reason completed habits fold in
 * place: ending the day looking at an empty list is the opposite of the
 * feeling this app is for. They drop off on their own when the day turns
 * over, so the list can never grow into a backlog of finished work.
 */
export function completedToday(state: DerivedState, today: StudyDate): TaskView[] {
  return Object.values(state.tasks)
    .filter((task) => !task.archived && task.done && task.completedOn === today)
    .map((task) => toTaskView(task, today));
}

/**
 * How much of what was promised got kept, over the last `days` days.
 *
 * Three deliberate choices, each of which changes the number:
 *
 *  • **Today is excluded.** The day is not over. Counting this morning's
 *    untouched habits as broken promises means the rate opens every day at its
 *    worst and climbs back — an app that greets you with your own failure.
 *  • **It starts at the first habit**, not `days` ago, so someone three days in
 *    isn't averaged against a week of days they had no habits for. `days` on
 *    the result says how much history actually backed the number.
 *  • **Archived habits count for neither side.** Archiving is withdrawing the
 *    promise, not failing it, and it must not retroactively make past weeks
 *    look worse than they were lived.
 */
export function commitment(
  state: DerivedState,
  today: StudyDate,
  days: number,
  opts: { habitId?: string } = {},
): Commitment {
  const none: Commitment = { kept: 0, promised: 0, ratio: 0, days: 0 };

  const habits = Object.values(state.habits).filter(
    (habit) => !habit.archived && (opts.habitId === undefined || habit.id === opts.habitId),
  );
  if (habits.length === 0 || days <= 0) return none;

  const first = habits.reduce(
    (earliest, habit) => (habit.createdOn < earliest ? habit.createdOn : earliest),
    habits[0]?.createdOn as StudyDate,
  );

  const end = shiftDate(today, -1);
  if (end < first) return none;

  const asked = shiftDate(end, -(days - 1));
  const start = asked < first ? first : asked;
  const counted = daysBetween(start, end) + 1;

  let kept = 0;
  let promised = 0;
  for (const date of dateRange(end, counted)) {
    for (const habit of habits) {
      promised += expectedWeight(habit, date);
      if (isDone(state, habit.id, date)) kept += 1;
    }
  }

  return {
    kept,
    promised,
    ratio: promised <= 0 ? 0 : Math.min(1, kept / promised),
    days: counted,
  };
}

/**
 * ULIDs sort by creation time, so newest-first is a string compare. No clock,
 * and no reliance on `ts`, which a device with a wrong clock could scramble.
 */
const newestFirst = (a: { id: string }, b: { id: string }) =>
  a.id < b.id ? 1 : a.id > b.id ? -1 : 0;

/**
 * Roll a set of entries up into a day's evidence.
 *
 * The one implementation of "what a day amounted to", so Home's card and
 * Khata's ledger can never disagree about a total — which they would, sooner
 * or later, if each did its own arithmetic.
 */
function rollUp(entries: Entry[], maxEntries: number): LogView {
  const byTrack = new Map<string, number>();
  let minutes = 0;
  let reviewMinutes = 0;
  let timedSessions = 0;

  for (const entry of entries) {
    minutes += entry.minutes;
    if (entry.kind === 'review') reviewMinutes += entry.minutes;
    if (entry.mode === 'timed') timedSessions += 1;
    byTrack.set(entry.track, (byTrack.get(entry.track) ?? 0) + entry.minutes);
  }

  const visible = maxEntries > 0 ? entries.slice(0, maxEntries) : entries;

  return {
    minutes,
    reviewMinutes,
    timedSessions,
    entries: visible.map((entry) => ({
      id: entry.id,
      track: entry.track,
      minutes: entry.minutes,
      kind: entry.kind,
      timed: entry.mode === 'timed',
      ...(entry.intention === undefined ? {} : { intention: entry.intention }),
    })),
    hiddenCount: Math.max(0, entries.length - visible.length),
    tracks: [...byTrack]
      .map(([track, trackMinutes]) => ({ track, minutes: trackMinutes }))
      .sort((a, b) => b.minutes - a.minutes),
  };
}

/** Every entry still standing, newest first. */
function liveEntries(state: DerivedState): Entry[] {
  return Object.values(state.entries)
    .filter((entry) => !entry.deleted)
    .sort(newestFirst);
}

/** What was recorded today, with its track breakdown. */
export function todayLog(state: DerivedState, today: StudyDate, maxEntries: number): LogView {
  return rollUp(
    liveEntries(state).filter((entry) => entry.date === today),
    maxEntries,
  );
}

/** One day of the ledger. */


/**
 * The ledger — Khata's whole content (§47).
 *
 * DAYS WITH NOTHING IN THEM ARE NOT RETURNED. Evidence of nothing is not
 * evidence, it is an accusation, and a screen that scrolls through blank days
 * is the confession booth §45 names as fatal. Absence is legible on Jatra's
 * grid, where kept-rate and the Comeback give it context; here the record is
 * only of what happened.
 *
 * `days` counts days that HAVE something, not calendar days — so "show 14
 * more" always yields fourteen more things to read rather than, for a student
 * returning after a month away, fourteen blanks.
 */
export function ledger(
  state: DerivedState,
  days: number,
  opts: { track?: string } = {},
): LedgerDay[] {
  const entries = opts.track
    ? liveEntries(state).filter((entry) => entry.track === opts.track)
    : liveEntries(state);

  const byDate = new Map<StudyDate, Entry[]>();
  for (const entry of entries) {
    const bucket = byDate.get(entry.date);
    if (bucket) bucket.push(entry);
    else byDate.set(entry.date, [entry]);
  }

  // Readings join the same days. A track filter applies to them through the
  // measure's own track, so filtering to Physics keeps the Physics mocks.
  const readingsByDate = new Map<StudyDate, LedgerReading[]>();
  const history = new Map<string, number[]>();
  for (const reading of Object.values(state.readings)
    .filter((item) => !item.deleted)
    .sort((a, b) => (a.date < b.date ? -1 : 1))) {
    const measure = state.measures[reading.measureId];
    if (!measure) continue;
    if (opts.track && measure.track !== opts.track) continue;

    const seen = history.get(measure.id) ?? [];
    const previous = seen[seen.length - 1];
    // Rank measures count DOWN to better, so the delta is flipped once here
    // and never again — no caller should have to know which way is good.
    const raw = previous === undefined ? null : reading.value - previous;
    const delta = raw === null ? null : measure.shape === 'rank' ? -raw : raw;
    history.set(measure.id, [...seen, reading.value]);

    const row: LedgerReading = {
      id: reading.id,
      measure: measure.name,
      value: reading.value,
      delta,
      ...(reading.outOf === undefined ? {} : { outOf: reading.outOf }),
      ...(measure.unit === undefined ? {} : { unit: measure.unit }),
    };
    const bucket = readingsByDate.get(reading.date);
    if (bucket) bucket.push(row);
    else readingsByDate.set(reading.date, [row]);
  }

  const dates = [...new Set([...byDate.keys(), ...readingsByDate.keys()])].sort().reverse();
  const visible = days > 0 ? dates.slice(0, days) : dates;

  return visible.map((date, index) => {
    // Days between this one and the newer one above it. Shown as a single
    // marker rather than blank rows: absence of evidence is not evidence, and
    // scrolling through empty days is the confession booth.
    const newer = visible[index - 1];
    const gapBefore = newer === undefined ? 0 : Math.max(0, daysBetween(date, newer) - 1);

    return {
      date,
      log: rollUp(byDate.get(date) ?? [], 0),
      readings: readingsByDate.get(date) ?? [],
      gapBefore,
    };
  });
}

/** Every track that has ever been logged, for the ledger's filter. */
export function loggedTracks(state: DerivedState): string[] {
  const seen = new Set<string>();
  for (const entry of liveEntries(state)) seen.add(entry.track);
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/**
 * Track names to offer when logging, most recently used first.
 *
 * Recency rather than lifetime volume: a student in exam week is logging the
 * paper they sit tomorrow, not the one they have spent the most hours on all
 * year. Tracks the Goal named at setup follow, so the very first log still
 * has something to tap instead of an empty field.
 *
 * There is no "manage tracks" screen and there will not be one — a track comes
 * into existence by being typed once, which is the whole of its lifecycle.
 */
export function recentTracks(state: DerivedState, limit: number): string[] {
  const seen: string[] = [];
  const push = (track: string) => {
    if (track && !seen.includes(track)) seen.push(track);
  };

  for (const entry of Object.values(state.entries).filter((e) => !e.deleted).sort(newestFirst)) {
    push(entry.track);
  }
  for (const goal of Object.values(state.goals)) {
    if (goal.status === 'active') goal.tracks.forEach(push);
  }

  return limit > 0 ? seen.slice(0, limit) : seen;
}

/**
 * The goal in progress, or null.
 *
 * The model holds many — `goals` is keyed by id and completed ones are kept —
 * but the UI at launch shows one. Multiplicity is cheap in the data and
 * expensive on screen: three goals turn "what do I do today" into an
 * attribution question, which is project management arriving without anyone
 * deciding to build it. The newest active one wins, so starting a goal makes
 * it the current one without a "set active" control to find.
 */
export function activeGoal(state: DerivedState, today: StudyDate): GoalView | null {
  const goal = Object.values(state.goals)
    .filter((item) => item.status === 'active')
    .sort((a, b) => (a.startedOn < b.startedOn ? 1 : -1))[0];
  if (!goal) return null;

  const partsTotal = goal.parts.length;
  const partsDone = goal.partsCompleted.length;
  const daysLeft = Math.max(0, daysBetween(today, goal.targetDate));
  const remaining = partsTotal - partsDone;
  const nextPart = goal.parts.find((part) => !goal.partsCompleted.includes(part)) ?? null;

  // How many parts an even pace would have finished by today. Elapsed is
  // clamped into the goal's own window so a goal opened late, or one already
  // past its date, cannot expect more than all of them.
  const span = daysBetween(goal.startedOn, goal.targetDate);
  const elapsed = Math.max(0, Math.min(span, daysBetween(goal.startedOn, today)));
  const expected = span <= 0 ? partsTotal : (partsTotal * elapsed) / span;

  return {
    id: goal.id,
    title: goal.title,
    category: goal.category,
    ...(goal.obstacle === undefined ? {} : { obstacle: goal.obstacle }),
    daysLeft,
    partsDone,
    partsTotal,
    parts: goal.parts.map((name) => ({ name, done: goal.partsCompleted.includes(name) })),
    progress: partsTotal === 0 ? null : partsDone / partsTotal,
    nextPart,
    daysPerPart: partsTotal === 0 || remaining <= 0 || daysLeft <= 0 ? null : daysLeft / remaining,
    drift: partsTotal === 0 ? null : Math.round(partsDone - expected),
  };
}

export function greetingFor(localHour: number): Greeting {
  if (localHour < 4) return 'night';
  if (localHour < 12) return 'morning';
  if (localHour < 17) return 'afternoon';
  if (localHour < 22) return 'evening';
  return 'night';
}

/**
 * Whether to show the "your streak is at risk" nudge.
 *
 * Gated on a live streak worth protecting, a late hour, and nothing done yet.
 * The moment they tick anything it disappears — §21 is clear the app should
 * not nag.
 */
export function streakAtRisk(
  state: DerivedState,
  features: FeatureFlags,
  today: StudyDate,
  localHour: number,
): boolean {
  const { enabled, afterHour, minStreak } = features.streakNudge;
  if (!enabled) return false;
  if (!isStreakAlive(state, today)) return false;
  if (state.streak.current < minStreak) return false;
  if (localHour < afterHour && localHour >= 4) return false;
  return (state.days[today]?.activities ?? 0) === 0;
}

/**
 * The state of a return.
 *
 * Present whenever the last thing that happened was a gap — from the first
 * moment of the day, before the user has done anything, right through to the
 * moment they tick something and it flips to `made`. It disappears the next
 * day, because by then continuing is just continuing.
 */
export type ComebackView = {
  /** Days missed immediately before today. At least 1. */
  missedDays: number;
  /** True once today has any activity — the return actually happened. */
  made: boolean;
};

/**
 * Is today a comeback, and has it been made yet? (§48 #3)
 *
 * Null in the two cases where there is nothing to come back from: a user with
 * no history at all, and a user who was active yesterday.
 *
 * Two sources, because the answer moves the instant the user acts. Before they
 * do anything today the gap is `today - lastActiveDate`; the moment they tick
 * something, today *becomes* the last active day and that arithmetic collapses
 * to zero — so past that point the size of the gap is read from the finalise
 * pass, which recorded it on the day itself.
 *
 * Note what is deliberately absent: any cap on how long the gap may be. A
 * student who disappears for five weeks of exams and opens the app again is
 * the most valuable person in the product to greet well, and telling them they
 * are too late to be welcomed back is how an app gets deleted.
 *
 * Deliberately absent too: the length of the streak they broke. Naming it
 * turns the card into a loss notice, which is the scoreboard mindset (§45) at
 * the exact moment the user is most likely to quit.
 *
 * Core has no clock, so `today` is the caller's.
 */
export function comeback(state: DerivedState, today: StudyDate): ComebackView | null {
  const stats = state.days[today];

  if ((stats?.activities ?? 0) > 0) {
    return stats?.comeback ? { missedDays: stats.missedBefore, made: true } : null;
  }

  const last = state.streak.lastActiveDate;
  if (!last) return null;

  const missedDays = dayIndex(today) - dayIndex(last) - 1;
  return missedDays >= 1 ? { missedDays, made: false } : null;
}

export type YearCell = {
  date: StudyDate;
  /** Habit-days the schedules asked for. 0 means a rest day, not a failure. */
  promised: number;
  kept: number;
  /** `kept / promised`, clamped. 0 when nothing was promised. */
  ratio: number;
  isToday: boolean;
  future: boolean;
};

/**
 * A year, as one picture.
 *
 * The wall chart answers "which promise am I keeping?" over a few weeks. This
 * answers a different question at a different scale — "what does this year
 * look like?" — which is why the two are not a duplication: one is habit × day
 * over a month, this is every day of a year with the habits collapsed away.
 */
export type YearGrid = {
  /** Columns of 7, oldest first, each starting on a Sunday. */
  weeks: YearCell[][];
  /** Column indices where a new month begins, for the header row. */
  months: { column: number; date: StudyDate }[];
  /** Days where something was promised and all of it kept. */
  perfectDays: number;
  /** Days where something was promised and at least some of it kept. */
  activeDays: number;
};

/** 0 = Sunday, matching the Bangladeshi week start. 1970-01-01 was a Thursday. */
const weekday = (date: StudyDate) => (dayIndex(date) + 4) % 7;

export function yearGrid(state: DerivedState, today: StudyDate, weeks = 53): YearGrid {
  const habits = Object.values(state.habits);

  // End on this week's Saturday so the final column is a whole week, with the
  // days after today drawn as future rather than as misses.
  const end = shiftDate(today, 6 - weekday(today));
  const total = Math.max(1, weeks) * 7;
  const start = shiftDate(end, -(total - 1));

  const columns: YearCell[][] = [];
  const months: { column: number; date: StudyDate }[] = [];
  let perfectDays = 0;
  let activeDays = 0;
  let lastMonth = '';

  for (let column = 0; column * 7 < total; column += 1) {
    const cells: YearCell[] = [];

    for (let day = 0; day < 7; day += 1) {
      const date = shiftDate(start, column * 7 + day);

      if (date > today) {
        cells.push({ date, promised: 0, kept: 0, ratio: 0, isToday: false, future: true });
        continue;
      }

      // A habit that did not exist yet cannot have been missed, and an
      // archived one is a promise withdrawn rather than broken — the same
      // rule `commitment` uses, so the year and the rate agree.
      const live = habits.filter(
        (habit) => !habit.archived && habit.createdOn <= date && isScheduledOn(habit, date),
      );
      const kept = live.filter((habit) => isDone(state, habit.id, date)).length;
      const promised = live.length;
      const ratio = promised === 0 ? 0 : Math.min(1, kept / promised);

      if (promised > 0 && kept > 0) activeDays += 1;
      if (promised > 0 && kept >= promised) perfectDays += 1;

      cells.push({ date, promised, kept, ratio, isToday: date === today, future: false });
    }

    const first = cells[0]?.date ?? start;
    const month = first.slice(0, 7);
    if (month !== lastMonth) {
      months.push({ column, date: first });
      lastMonth = month;
    }

    columns.push(cells);
  }

  return { weeks: columns, months, perfectDays, activeDays };
}

/**
 * One thing worth going back over.
 *
 * The unit is the TOPIC, not the track and not the session. A topic is named
 * by whatever the student called the work — a focus session's intention — and
 * falls back to the track when there was none. So a three-tap manual log still
 * produces a real (if coarse) item, and a student who uses Focus gets sharper
 * scheduling as a consequence of working, never as a setting they had to find.
 */
export type ReviewItem = {
  /** `intention` when there was one, otherwise the track. */
  topic: string;
  track: string;
  /** The last time this was touched at all, learned or reviewed. */
  lastSeen: StudyDate;
  /** How many reviews have happened — which rung of the ladder it is on. */
  reviews: number;
  dueOn: StudyDate;
  /** 0 on the day it comes due, growing after. */
  overdueDays: number;
};

export type ReviewPlan = {
  /** Most overdue first, capped by the ruleset. */
  items: ReviewItem[];
  /** Rough budget for the whole proposal. */
  minutes: number;
  /** Due today but deliberately not proposed. Never rendered as a backlog. */
  hiddenCount: number;
  /**
   * Set when every proposed item shares a track.
   *
   * That is the interleaving case (§43, g = 0.42, strongest for SIMILAR
   * categories): three chapters of one subject belong in one mixed sitting,
   * not three separate lines on the screen.
   */
  track: string | null;
};

// Track and topic as a JSON pair rather than a joined string. A delimiter
// has to be a character neither field can contain, and the previous choice —
// a raw NUL byte, written literally into the source — made this entire file
// read as binary to grep, diff and most editors. JSON escapes its own
// contents, so the key is collision-free with no sentinel at all.
const topicKey = (topic: string, track: string) =>
  JSON.stringify([track, topic.toLowerCase()]);

/**
 * What to revise today (§48 #10) — the differentiator.
 *
 * Reads entries that already exist and proposes a mixed, spaced sitting. The
 * student never authors a card, never builds a deck, and never opts in: they
 * study, and a line appears on Today when something is due.
 *
 * Null when nothing is due, which is most days early on. Silence is the
 * correct output — a review card that always says something teaches students
 * to stop reading it.
 */
export function reviewPlan(
  state: DerivedState,
  today: StudyDate,
  ruleset: Ruleset,
): ReviewPlan | null {
  const { ladder, minutesPerItem, maxItems, maxMinutes } = ruleset.review;
  if (ladder.length === 0) return null;

  type Seen = { topic: string; track: string; lastSeen: StudyDate; reviews: number };
  const topics = new Map<string, Seen>();

  // Oldest first, so `lastSeen` ends up holding the most recent touch.
  const entries = Object.values(state.entries)
    .filter((entry) => !entry.deleted)
    .sort((a, b) => (a.id < b.id ? -1 : 1));

  for (const entry of entries) {
    const topic = entry.intention?.trim() || entry.track;
    const key = topicKey(topic, entry.track);
    const current = topics.get(key);

    topics.set(key, {
      topic,
      track: entry.track,
      lastSeen: entry.date,
      // Only a review advances the ladder. Studying the same thing twice in a
      // week is not spacing, and must not push the next review further out.
      reviews: (current?.reviews ?? 0) + (entry.kind === 'review' ? 1 : 0),
    });
  }

  const due: ReviewItem[] = [];

  for (const seen of topics.values()) {
    // The last rung repeats: a topic reviewed six times still comes back.
    const gap = ladder[Math.min(seen.reviews, ladder.length - 1)] as number;
    const dueOn = shiftDate(seen.lastSeen, gap);
    const overdueDays = dayIndex(today) - dayIndex(dueOn);
    if (overdueDays < 0) continue;

    due.push({ ...seen, dueOn, overdueDays });
  }

  if (due.length === 0) return null;

  // Most overdue first: the thing decaying longest is the thing worth the
  // next ten minutes.
  due.sort((a, b) => (b.overdueDays === a.overdueDays
    ? a.topic.localeCompare(b.topic)
    : b.overdueDays - a.overdueDays));

  const budget = Math.max(1, Math.floor(maxMinutes / Math.max(1, minutesPerItem)));
  const items = due.slice(0, Math.max(1, Math.min(maxItems, budget)));
  const track = items.every((item) => item.track === items[0]?.track)
    ? (items[0]?.track ?? null)
    : null;

  return {
    items,
    minutes: Math.min(maxMinutes, items.length * minutesPerItem),
    hiddenCount: due.length - items.length,
    track,
  };
}

/**
 * Labels already used on a track, most recent first.
 *
 * Offered as chips when starting a session, because free text fragments: "ch.4",
 * "ch 4" and "chapter 4" would otherwise become three topics on three separate
 * schedules. Reuse has to be a tap or the scheduler quietly degrades.
 */
export function recentTopics(state: DerivedState, track: string, limit: number): string[] {
  const seen: string[] = [];
  for (const entry of Object.values(state.entries)
    .filter((e) => !e.deleted && e.track === track && e.intention)
    .sort((a, b) => (a.id < b.id ? 1 : -1))) {
    const label = entry.intention?.trim();
    if (label && !seen.includes(label)) seen.push(label);
  }
  return limit > 0 ? seen.slice(0, limit) : seen;
}

/**
 * The record of returning — the north-star metric (§51), per student.
 *
 * A *gap* is a run of inactive days between two active ones, or a run still
 * running now. A gap that has been closed is a comeback. This says how many of
 * them there have been and how many were closed, which is the one statistic in
 * the product that describes the behaviour the evidence says actually moves.
 *
 * Streak length is a symptom; this is the cause. It is also the number that
 * cannot be destroyed by a bad week, which is precisely why it belongs on the
 * screen a student visits when they are wondering whether to give up.
 */
export type ComebackRecord = {
  /** Gaps closed by coming back. */
  returns: number;
  /** Gaps in total, counting one still open. */
  gaps: number;
  /** `returns / gaps`. 1 when there have never been any gaps — a student who
   *  has never missed has never failed to return. */
  rate: number;
  /** Inside an unclosed gap right now. */
  away: boolean;
  /** Days missed in the longest gap ever closed. The proof that a return is
   *  possible from further out than wherever they are today. */
  longestReturn: number;
};

export function comebackRecord(state: DerivedState, today: StudyDate): ComebackRecord {
  let returns = 0;
  let longestReturn = 0;

  for (const day of Object.values(state.days)) {
    if (!day.comeback) continue;
    returns += 1;
    longestReturn = Math.max(longestReturn, day.missedBefore);
  }

  const last = state.streak.lastActiveDate;
  const away = last !== null && dayIndex(today) - dayIndex(last) > 1;
  const gaps = returns + (away ? 1 : 0);

  return { returns, gaps, rate: gaps === 0 ? 1 : returns / gaps, away, longestReturn };
}

/**
 * Minutes over the last seven days against the seven before them.
 *
 * A trailing pair rather than calendar weeks, so the comparison works on a
 * Tuesday and needs no week-start setting. `delta` is the honest difference,
 * including when it is negative — a tracker that only reports improvement is
 * a tracker nobody believes the rest of the time.
 */
export type MinutesTrend = {
  minutes: number;
  previous: number;
  delta: number;
  /** Null until there is a previous week to compare against. */
  ratio: number | null;
};

export function minutesTrend(state: DerivedState, today: StudyDate, span = 7): MinutesTrend {
  const sum = (end: StudyDate, count: number) =>
    dateRange(end, count).reduce((total, date) => total + (state.days[date]?.minutes ?? 0), 0);

  const minutes = sum(today, span);
  const previous = sum(shiftDate(today, -span), span);

  return {
    minutes,
    previous,
    delta: minutes - previous,
    ratio: previous > 0 ? minutes / previous : null,
  };
}

/**
 * The band of numbers around the wall chart on desktop Home.
 *
 * These are the diagnostic statistics of §53.6 — each one changes what the
 * student does next — and the composition is as deliberate as the contents.
 * Kept-rate leads because it is a fact about the person that survives a bad
 * day. The streak is present but is NOT the headline: §45 names "the biggest
 * number on screen is a streak" as the tell of the scoreboard mindset, which
 * is the failure this product is shaped to avoid.
 *
 * Desktop only, and that is a rule rather than a layout accident. The phone is
 * the daily loop and gets one number (§53.6); the chart and its numbers are
 * the weekly artifact, and they live together.
 */
export type HomeStats = {
  /** Null until there is enough history for a percentage to mean anything. */
  commitment: Commitment | null;
  comebacks: ComebackRecord;
  minutes: MinutesTrend;
  streak: { current: number; best: number; alive: boolean };
};

export function homeStats(
  state: DerivedState,
  today: StudyDate,
  features: FeatureFlags,
): HomeStats {
  const kept = commitment(state, today, features.home.commitmentDays);

  return {
    commitment: kept.days >= MIN_COMMITMENT_DAYS && kept.promised > 0 ? kept : null,
    comebacks: comebackRecord(state, today),
    minutes: minutesTrend(state, today),
    streak: {
      current: state.streak.current,
      best: state.streak.longest,
      alive: isStreakAlive(state, today),
    },
  };
}

export type PointView = {
  id: string;
  /** The number as recorded — 62, or 12 for a rank. */
  value: number;
  /** The denominator in force for this point. */
  outOf?: number;
  /**
   * What the chart plots, 0–1, comparable across differing denominators.
   *
   * For `outOf`, the fraction. For `rank`, inverted so that better is always
   * higher on the page — 1st of 340 plots at the top, exactly where a student
   * expects their best result to be. For `count`, normalised against the
   * largest value in the series, because a bare page count has no ceiling.
   */
  plot: number;
  date: StudyDate;
  entryId?: string;
};

/**
 * A measure and everything recorded for it — the contract the chart reads.
 *
 * Deliberately not marks-shaped. The trend row and sparkline take this, so a
 * second kind of series later is a new source selector and no new rendering.
 */
export type SeriesView = {
  id: string;
  label: string;
  shape: MeasureShape;
  track?: string;
  unit?: string;
  /** Oldest → newest. */
  points: PointView[];
  latest: PointView;
  /**
   * Change against the previous point, in the units a reader expects:
   * percentage points for `outOf`, places for `rank` (positive = moved up),
   * raw units for `count`. Null when there is nothing to compare against.
   */
  delta: number | null;
  /**
   * Whether this series is allowed to say the student got better (§53.8).
   *
   * True only for `outOf` and `rank` attached to a track. A count of pages or
   * kilograms records and does not judge — the app has no business deciding
   * whether that number going down is good news.
   */
  assertsSkill: boolean;
};

/** Every measure with at least one reading, most recently updated first. */
export function series(state: DerivedState): SeriesView[] {
  const readings = Object.values(state.readings)
    .filter((reading) => !reading.deleted)
    .sort((a, b) => (a.date === b.date ? (a.id < b.id ? -1 : 1) : a.date < b.date ? -1 : 1));

  const byMeasure = new Map<string, typeof readings>();
  for (const reading of readings) {
    const bucket = byMeasure.get(reading.measureId);
    if (bucket) bucket.push(reading);
    else byMeasure.set(reading.measureId, [reading]);
  }

  const out: SeriesView[] = [];

  for (const [measureId, group] of byMeasure) {
    const measure = state.measures[measureId];
    if (!measure || group.length === 0) continue;

    const peak = Math.max(...group.map((reading) => reading.value), 1);

    const points: PointView[] = group.map((reading) => {
      const outOf = reading.outOf ?? measure.outOf;
      const plot =
        measure.shape === 'count'
          ? reading.value / peak
          : outOf && outOf > 0
            ? measure.shape === 'rank'
              ? // Inverted: 1st of 340 belongs at the top of the chart.
                1 - (reading.value - 1) / Math.max(1, outOf - 1)
              : reading.value / outOf
            : 0;

      return {
        id: reading.id,
        value: reading.value,
        plot: Math.max(0, Math.min(1, plot)),
        date: reading.date,
        ...(outOf === undefined ? {} : { outOf }),
        ...(reading.entryId === undefined ? {} : { entryId: reading.entryId }),
      };
    });

    const latest = points[points.length - 1] as PointView;
    const prior = points.length > 1 ? (points[points.length - 2] as PointView) : null;

    let delta: number | null = null;
    if (prior) {
      if (measure.shape === 'outOf') {
        delta = Math.round((latest.plot - prior.plot) * 100);
      } else if (measure.shape === 'rank') {
        // Places gained. Moving from 40th to 12th is +28, which is the way a
        // student says it out loud.
        delta = prior.value - latest.value;
      } else {
        delta = latest.value - prior.value;
      }
    }

    out.push({
      id: measure.id,
      label: measure.name,
      shape: measure.shape,
      points,
      latest,
      delta,
      assertsSkill: measure.shape !== 'count' && measure.track !== undefined,
      ...(measure.track === undefined ? {} : { track: measure.track }),
      ...(measure.unit === undefined ? {} : { unit: measure.unit }),
    });
  }

  return out.sort((a, b) => (a.latest.date < b.latest.date ? 1 : -1));
}

/**
 * যাত্রা — who you are becoming (§47).
 *
 * Deliberately NOT "stats". Everything here is either an identity (rank,
 * titles) or a fact that survives a bad day (kept-rate, comeback record) —
 * nothing that a single missed morning can take away, because this is the
 * screen someone opens in the week they are thinking of quitting.
 *
 * Visited about weekly, on purpose. Daily checking of a long-term number is
 * the scoreboard mindset; weekly perception of progress is what the evidence
 * rewards (§43).
 */
export type JatraView = {
  power: number;
  rank: RankId;
  rankRatio: number;
  titles: TitleId[];
  streak: { current: number; best: number; alive: boolean };
  /** Null until there is enough history for a percentage to mean anything. */
  commitment: Commitment | null;
  comebacks: ComebackRecord;
  goal: GoalView | null;
  /** Progression as a route, so it can be shown without a scoreboard. */
  path: JourneyPath;
};

export function jatraView(
  state: DerivedState,
  today: StudyDate,
  features: FeatureFlags,
  ruleset: Ruleset,
): JatraView {
  const kept = commitment(state, today, features.home.commitmentDays);

  return {
    power: state.power,
    rank: state.rank,
    rankRatio: state.rankRatio,
    titles: state.titles,
    streak: {
      current: state.streak.current,
      best: state.streak.longest,
      alive: isStreakAlive(state, today),
    },
    commitment: kept.days >= MIN_COMMITMENT_DAYS && kept.promised > 0 ? kept : null,
    comebacks: comebackRecord(state, today),
    goal: activeGoal(state, today),
    path: journeyPath(state, ruleset),
  };
}

/**
 * Everything the Home screen needs, in one tested call.
 *
 * Home reads exactly this — no assembling from six places, no component
 * reaching into `state.days`. Adding or removing a Home element is a change
 * here plus a flag, not a refactor.
 */
export function todayView(
  state: DerivedState,
  today: StudyDate,
  localHour: number,
  features: FeatureFlags,
  _ruleset: Ruleset,
): TodayView {
  // The cap applies to OPEN work only. Ticking a task must never push another
  // open one out of sight, and what's already done is bounded by the day.
  const open = features.home.showTasks ? openTasks(state, today) : [];
  const visible = open.slice(0, features.home.maxTasks);
  const done = features.home.showTasks ? completedToday(state, today) : [];

  const habits = orderedHabits(state)
    .filter((habit) => !habit.archived && habit.createdOn <= today)
    .map((habit) => {
      const level = completionAt(state, habit.id, today);
      return {
        id: habit.id,
        name: habit.name,
        done: level !== null,
        level,
        scheduledToday: isScheduledOn(habit, today),
        history: features.home.showRowHistory
          ? habitHistory(state, habit.id, today, features.home.rowHistoryDays)
          : [],
        ...(habit.icon === undefined ? {} : { icon: habit.icon }),
        ...(habit.floor === undefined ? {} : { floor: habit.floor }),
        ...(habit.anchor === undefined ? {} : { anchor: habit.anchor }),
        ...(habit.place === undefined ? {} : { place: habit.place }),
      };
    });

  const scheduled = habits.filter((habit) => habit.scheduledToday);

  // A percentage off two days is noise dressed as a fact, so the whole line
  // stays hidden until there is a week behind it.
  const kept = features.home.showCommitment
    ? commitment(state, today, features.home.commitmentDays)
    : null;

  return {
    date: today,
    greeting: greetingFor(localHour),
    name: state.profileName,
    streak: {
      current: state.streak.current,
      best: state.streak.longest,
      alive: isStreakAlive(state, today),
    },
    commitment: kept && kept.days >= MIN_COMMITMENT_DAYS && kept.promised > 0 ? kept : null,
    power: state.power,
    rank: state.rank,
    rankRatio: state.rankRatio,
    titles: state.titles,
    habits,
    progress: { done: scheduled.filter((habit) => habit.done).length, total: scheduled.length },
    tasks: { items: [...visible, ...done], hiddenCount: Math.max(0, open.length - visible.length) },
    minutesToday: state.days[today]?.minutes ?? 0,
    log: features.home.showTodayLog
      ? todayLog(state, today, features.home.maxEntries)
      : null,
    goal: features.home.showGoalLine ? activeGoal(state, today) : null,
    streakAtRisk: streakAtRisk(state, features, today, localHour),
    comeback: features.home.showComeback ? comeback(state, today) : null,
  };
}

/**
 * When this habit actually happens, once there is enough evidence to say.
 *
 * The point of this selector is to turn the anchor question from composition
 * into confirmation. Asked to invent a plan on day one, a student guesses;
 * shown "you usually do this in the evening — make that the plan?", they tap
 * once, and the answer is true because it is a description of what they have
 * already been doing.
 *
 * The MEDIAN rather than the mean: one 2am cram should not drag a habit that
 * otherwise happens after dinner into the middle of the night.
 */
export type Rhythm = {
  /** Local hour, 0–23. */
  hour: number;
  samples: number;
};

/** Below this the "usual" time is noise, and proposing it would be a guess
 *  wearing the costume of an observation. */
export const RHYTHM_MIN_SAMPLES = 3;

export function habitRhythm(state: DerivedState, habitId: string): Rhythm | null {
  const hours = state.habits[habitId]?.recentHours ?? [];
  if (hours.length < RHYTHM_MIN_SAMPLES) return null;

  const sorted = [...hours].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const hour =
    sorted.length % 2 === 1
      ? (sorted[middle] as number)
      : Math.round(((sorted[middle - 1] as number) + (sorted[middle] as number)) / 2);

  return { hour, samples: hours.length };
}

/**
 * Habits whose plan could now be proposed: performed enough times to have a
 * rhythm, and without an anchor already.
 *
 * Returned rather than picked so the caller decides how many to ask about —
 * which, given the coach's one-flow-per-session budget, is one.
 */
export function habitsAwaitingPlan(state: DerivedState): string[] {
  return orderedHabits(state)
    .filter((habit) => !habit.archived && habit.anchor === undefined)
    .filter((habit) => habitRhythm(state, habit.id) !== null)
    .map((habit) => habit.id);
}

/**
 * Habits that have been missed and have no floor.
 *
 * The floor's whole purpose is to survive a bad day, so the first bad day is
 * when the question becomes answerable. Asked at creation it is a solution to
 * a problem the student has not had.
 */
export function habitsAwaitingFloor(state: DerivedState, today: StudyDate): string[] {
  return orderedHabits(state)
    .filter((habit) => !habit.archived && habit.floor === undefined)
    .filter((habit) => {
      // Yesterday only. A habit missed three weeks ago is not a live wound,
      // and asking about it reads as the app keeping a grudge.
      const yesterday = shiftDate(today, -1);
      if (!isScheduledOn(habit, yesterday)) return false;
      return completionAt(state, habit.id, yesterday) === null;
    })
    .map((habit) => habit.id);
}

/**
 * The journey as a route, not a number.
 *
 * Progression is the thing students ask for most directly — "levelling up
 * like a game" — and the temptation is to answer it with a big numeral, which
 * is the scoreboard §53.8 exists to prevent. A PATH answers it better and
 * costs nothing in risk: stops already passed are dated history, so a bad
 * fortnight cannot move anyone backwards along it. It only ever grows.
 *
 * The next stop is NAMED, with a distance. "340 to Vanguard" is the unit that
 * actually motivates; "2,340 XP" is a number about nothing.
 */
export type PathStop = {
  id: RankId;
  minPower: number;
  state: 'passed' | 'current' | 'ahead';
};

export type JourneyPath = {
  stops: PathStop[];
  /** The stop being walked toward, and what is left to reach it. Null at the
   *  end of the ladder — where the honest thing to show is nothing. */
  next: { id: RankId; remaining: number } | null;
};

export function journeyPath(state: DerivedState, ruleset: Ruleset): JourneyPath {
  const currentIndex = ruleset.ranks.findIndex((tier) => tier.id === state.rank);
  const stops: PathStop[] = ruleset.ranks.map((tier, index) => ({
    id: tier.id,
    minPower: tier.minPower,
    state: index < currentIndex ? 'passed' : index === currentIndex ? 'current' : 'ahead',
  }));

  const upcoming = ruleset.ranks[currentIndex + 1];
  return {
    stops,
    next:
      upcoming === undefined
        ? null
        : { id: upcoming.id, remaining: Math.max(0, upcoming.minPower - state.power) },
  };
}

/** One recorded number, as the ledger shows it. */
export type LedgerReading = {
  id: string;
  measure: string;
  value: number;
  outOf?: number;
  unit?: string;
  /** Change from the previous reading of the same measure, or null for the
   *  first. Direction is already corrected for rank measures, where down is
   *  better — the caller never has to know which way is good. */
  delta: number | null;
};

/**
 * The record for one day: what was studied, and what it scored.
 *
 * Readings live here rather than on their own screen because a reading IS
 * dated evidence, exactly like an entry. Splitting them into two places would
 * make the student remember which drawer a thing went into, and it would
 * break the one claim the ledger makes: that it holds everything that
 * happened, in order.
 */
export type LedgerDay = {
  date: StudyDate;
  log: LogView;
  readings: LedgerReading[];
  /** Days with nothing in them between this day and the newer one above it.
   *  Rendered as a marker, never as blank rows — see `ledger`. */
  gapBefore: number;
};

/** What a month of the ledger adds up to. */
export type LedgerSummary = {
  minutes: number;
  entries: number;
  /** The track with the most minutes, or null when nothing is logged. */
  topTrack: string | null;
};

export function ledgerSummary(state: DerivedState, since: StudyDate): LedgerSummary {
  const entries = liveEntries(state).filter((entry) => entry.date >= since);
  const byTrack = new Map<string, number>();
  let minutes = 0;

  for (const entry of entries) {
    minutes += entry.minutes;
    byTrack.set(entry.track, (byTrack.get(entry.track) ?? 0) + entry.minutes);
  }

  const top = [...byTrack].sort((a, b) => b[1] - a[1])[0];
  return { minutes, entries: entries.length, topTrack: top?.[0] ?? null };
}

/**
 * Goals that are finished, newest first.
 *
 * The centrepiece of Me. A completed goal is the single best thing this app
 * can show anyone, and it is the one class of fact that no later bad week can
 * touch — which is what makes it belong on the screen for what is settled
 * rather than on the one for what is in flight.
 */
export type FinishedGoal = { id: string; title: string; category: GoalCategory; on: StudyDate };

export function finishedGoals(state: DerivedState): FinishedGoal[] {
  return Object.values(state.goals)
    .filter((goal) => goal.status === 'completed')
    .map((goal) => ({
      id: goal.id,
      title: goal.title,
      category: goal.category,
      on: goal.targetDate,
    }))
    .sort((a, b) => (a.on < b.on ? 1 : -1));
}

/**
 * The lifetime numbers for Me.
 *
 * Every one of these only ever goes UP. That is the entire selection rule for
 * this screen: nothing here can be reduced by a bad fortnight, so opening it
 * is never an audit. Anything that can fall — kept-rate, the streak, pace —
 * belongs on Jatra with the context that explains it.
 */
export type Lifetime = {
  daysRecorded: number;
  minutes: number;
  promisesKept: number;
  goalsCompleted: number;
};

export function lifetime(state: DerivedState): Lifetime {
  return {
    daysRecorded: Object.keys(state.days).length,
    minutes: state.totals.minutes,
    promisesKept: state.totals.habitsCompleted,
    goalsCompleted: state.totals.goalsCompleted,
  };
}

/**
 * The weekly review (§48 #6) — what the week actually was.
 *
 * Four facts and one question, and the shape matters more than the numbers.
 * It opens with what was KEPT, not with what slipped, because a review that
 * leads with failure is an audit and gets avoided; and it names exactly ONE
 * habit to think about, because a list of five things to fix is a list nobody
 * acts on.
 *
 * The question it ends on — what got in the way — is the same one the coach
 * asks after a miss, and it writes to the same place: the goal's obstacle.
 * That is the loop closing. Evidence becomes a better promise only if
 * something reads the evidence and changes the plan.
 */
export type WeeklyReview = {
  /** The seven days under review, oldest first. */
  from: StudyDate;
  to: StudyDate;
  kept: number;
  promised: number;
  ratio: number;
  /** Change in ratio against the seven days before, or null with no prior
   *  week to compare against. */
  delta: number | null;
  minutes: number;
  /** The one habit that slipped most, or null when nothing did. */
  slipped: { id: string; name: string; kept: number; promised: number } | null;
  /** What the student last said gets in the way, to be confirmed or replaced. */
  obstacle: string | null;
};

/** Days a review covers. A week, because that is the unit people plan in. */
export const REVIEW_WINDOW_DAYS = 7;

export function weeklyReview(state: DerivedState, today: StudyDate): WeeklyReview | null {
  const habits = orderedHabits(state).filter((habit) => !habit.archived);
  if (habits.length === 0) return null;

  const from = shiftDate(today, -REVIEW_WINDOW_DAYS);
  const to = shiftDate(today, -1);
  const window = commitment(state, to, REVIEW_WINDOW_DAYS);
  if (window.promised === 0) return null;

  const previous = commitment(state, shiftDate(to, -REVIEW_WINDOW_DAYS), REVIEW_WINDOW_DAYS);

  let minutes = 0;
  for (const entry of liveEntries(state)) {
    if (entry.date >= from && entry.date <= to) minutes += entry.minutes;
  }

  // The habit with the widest gap between promised and kept. Ties go to the
  // one promised more often, so the answer is about the habit that mattered
  // most rather than whichever happens to sort first.
  let slipped: WeeklyReview['slipped'] = null;
  for (const habit of habits) {
    const own = commitment(state, to, REVIEW_WINDOW_DAYS, { habitId: habit.id });
    if (own.promised === 0) continue;
    const missed = own.promised - own.kept;
    if (missed <= 0) continue;
    const worst = slipped === null ? -1 : slipped.promised - slipped.kept;
    if (missed > worst) {
      slipped = {
        id: habit.id,
        name: habit.name,
        kept: Math.round(own.kept),
        promised: Math.round(own.promised),
      };
    }
  }

  const goal = Object.values(state.goals).find((item) => item.status === 'active');

  return {
    from,
    to,
    kept: Math.round(window.kept),
    promised: Math.round(window.promised),
    ratio: window.ratio,
    delta: previous.promised === 0 ? null : window.ratio - previous.ratio,
    minutes,
    slipped,
    obstacle: goal?.obstacle ?? null,
  };
}

/**
 * The register this student's app speaks in.
 *
 * Their own answer first, then the shape of what they are actually working
 * on, then the default. Deliberately a SELECTOR rather than a stored field:
 * it is a reading of the record, so a student whose answer and whose goals
 * disagree is described by their answer, and one who never answered is
 * described by their work.
 */
export function flavourOf(state: DerivedState): Flavour {
  if (state.profilePath !== null) return flavourForPath(state.profilePath);

  const goal = Object.values(state.goals)
    .filter((item) => item.status === 'active')
    .sort((a, b) => (a.startedOn < b.startedOn ? 1 : -1))[0];

  return flavourFor(goal === undefined ? {} : { category: goal.category });
}
